import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../Context/Can';
import { getBusinessDashboard, refreshBusinessDashboardCache, downloadBusinessDashboard } from '../../Redux/actions';
import { Box, Grid, Tabs, Tab, Snackbar, Alert } from '@mui/material';
import { exportBusinessDashboardToExcel } from '../../utils/Common';
import { updateParams } from '../../utils';

// Tab Components
import Overview from './tabs/Overview';
import Opportunity from './tabs/Opportunity';
import ProjectManagement from './tabs/ProjectManagement';
import InvoiceSchedule from './tabs/InvoiceSchedule';
import Renewal from './tabs/Renewal';
import Collections from './tabs/Collections';
import AlertsTickets from './tabs/AlertsTickets';
import MasterData from './tabs/MasterData';
import MasterSchedules from './tabs/MasterSchedules';
import ProjectTimeline from './tabs/ProjectTimeline';

export default function BusinessDashboard() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();
    const { tabId } = useParams();
    const existingParams = Object.fromEntries([...queryParams]);
    // Admins see every tab. SPOC users only get PROJECT TIMELINE: the other tabs hold commercial data.
    const ability = useAbility(AbilityContext);
    const fullAccess = ability.can('view', 'Announcements');

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [totalClients, setTotalClients] = useState(0);
    const [totalClientGroups, setTotalClientGroups] = useState(0);
    const [totalFacilities, setTotalFacilities] = useState(0);
    const [totalSalesOrders, setTotalSalesOrders] = useState(0);
    const [totals, setTotals] = useState({ totalContractValue: 0, totalInvoiced: 0, totalOutstanding: 0 });
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [isSearchOrFilterApplied, setIsSearchOrFilterApplied] = useState(false);
    const [bdFilterOptions, setBdFilterOptions] = useState({});
    const [notice, setNotice] = useState({ message: '', severity: 'warning' });
    
    const tabNames = ['overview', 'opportunity', 'project-management', 'invoice-schedule', 'renewal', 'collections', 'alerts-tickets', 'schedules', 'master-data', 'project-timeline'];
    const HIDDEN_TAB_INDICES = [];
    const PROJECT_TIMELINE_TAB = 9;
    const getTabIndexFromUrl = () => {
        if (!fullAccess) return PROJECT_TIMELINE_TAB;
        const tabParam = tabId || 'overview';
        const index = tabNames.indexOf(tabParam);
        return (index >= 0 && !HIDDEN_TAB_INDICES.includes(index)) ? index : 0; // Default to overview
    };
    const [activeTab, setActiveTab] = useState(getTabIndexFromUrl());

    const currentPage = parseInt(existingParams?.page) || 1;

    useEffect(() => {
        // Any other tab URL (including the default /business-dashboard/overview) goes to the timeline for SPOC users
        if (!fullAccess && tabId !== 'project-timeline') {
            navigate('/business-dashboard/project-timeline', { replace: true });
            return;
        }
        const tabIndex = getTabIndexFromUrl();
        setActiveTab(tabIndex);

        // Only add page parameter for master-data tab (index 8)
        if (tabIndex === 8 && !existingParams?.page) {
            const defaultParams = new URLSearchParams({ page: '1' });
            navigate(`/business-dashboard/${tabId || 'overview'}?${defaultParams.toString()}`, { replace: true });
            return;
        }
        if (tabIndex === 8) { // Only fetch data for master-data tab
            fetchDashboard();
        }
    }, [queryParams, tabId, fullAccess]);

    const fetchDashboard = () => {
        setLoading(true);
        const params = {
            page: isSearchOrFilterApplied ? 1 : (parseInt(existingParams?.page) || 1),
            per_page: 10,
            search: existingParams?.search || '',
            client_type: existingParams?.client_type || '',
            segment: existingParams?.segment || '',
            client_group: existingParams?.client_group || '',
            company_name: existingParams?.company_name || '',
            facility: existingParams?.facility || '',
            status: existingParams?.status || '',
            contract_type: existingParams?.contract_type || '',
            billing_cycle: existingParams?.billing_cycle || '',
            bd_owner: existingParams?.bd_owner || '',
            project_owner: existingParams?.project_owner || '',
            customer_status: existingParams?.customer_status || '',
            poFromDate: existingParams?.poFromDate || '',
            poToDate: existingParams?.poToDate || '',
            poExpiryFromDate: existingParams?.poExpiryFromDate || '',
            poExpiryToDate: existingParams?.poExpiryToDate || '',
            nextInvoiceFromDate: existingParams?.nextInvoiceFromDate || '',
            nextInvoiceToDate: existingParams?.nextInvoiceToDate || '',
            overdue_aging: existingParams?.overdue_aging || '',
        };
        setIsSearchOrFilterApplied(false);
        dispatch(getBusinessDashboard(params)).then((resp) => {
            if (resp?.data?.success) {
                setData(resp.data.data || []);
                setTotalPages(resp.data.totalPages || 1);
                setTotalClients(resp.data.totalClients || 0);
                setTotalClientGroups(resp.data.totalClientGroups || 0);
                setTotalFacilities(resp.data.totalFacilities || 0);
                setTotalSalesOrders(resp.data.totalSalesOrders || 0);
                setTotals(resp.data.totals || { totalContractValue: 0, totalInvoiced: 0, totalOutstanding: 0 });
                if (resp.data.filterOptions) {
                    setBdFilterOptions(resp.data.filterOptions);
                }
            }
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    };

    const updateQueryParams = (params) => {
        const searchParams = updateParams(existingParams, params);
        navigate(`/business-dashboard/${tabId || 'overview'}?${searchParams}`, { replace: true });
    };

    const handleSearch = (value) => {
        updateQueryParams({ search: value, page: 1 });
        setIsSearchOrFilterApplied(true);
    };

    const handlePageChange = (event, value) => {
        const newParams = { ...existingParams, page: value };
        const searchParams = new URLSearchParams(newParams).toString();
        navigate(`/business-dashboard/${tabId || 'overview'}?${searchParams}`);
    };

    const handleTabSwitch = () => {
        setIsSearchOrFilterApplied(true);
    };

    const handleRefreshCache = () => {
        setRefreshing(true);
        dispatch(refreshBusinessDashboardCache()).then((resp) => {
            if (resp?.data?.success) {
                fetchDashboard();
            } else if (resp?.data?.message) {
                setNotice({ message: resp.data.message, severity: 'info' });
            }
            setRefreshing(false);
        }).catch(() => {
            setRefreshing(false);
        });
    };

    const handleDownload = () => {
        setDownloading(true);
        const params = {
            client_type: existingParams?.client_type || '',
            segment: existingParams?.segment || '',
            client_group: existingParams?.client_group || '',
            company_name: existingParams?.company_name || '',
            facility: existingParams?.facility || '',
            status: existingParams?.status || '',
            contract_type: existingParams?.contract_type || '',
            billing_cycle: existingParams?.billing_cycle || '',
            bd_owner: existingParams?.bd_owner || '',
            project_owner: existingParams?.project_owner || '',
            customer_status: existingParams?.customer_status || '',
            poFromDate: existingParams?.poFromDate || '',
            poToDate: existingParams?.poToDate || '',
            poExpiryFromDate: existingParams?.poExpiryFromDate || '',
            poExpiryToDate: existingParams?.poExpiryToDate || '',
            nextInvoiceFromDate: existingParams?.nextInvoiceFromDate || '',
            nextInvoiceToDate: existingParams?.nextInvoiceToDate || '',
            overdue_aging: existingParams?.overdue_aging || '',
        };
        dispatch(downloadBusinessDashboard(params)).then((resp) => {
            if (resp?.data?.success) {
                exportBusinessDashboardToExcel(resp.data.data);
            }
            setDownloading(false);
        }).catch(() => {
            setDownloading(false);
        });
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        const newTab = tabNames[newValue];
        // Clear all search and filter params, reset to page 1
        navigate(`/business-dashboard/${newTab}`);
    };

    return (
        <div className='w3-padding-large'>
            <Grid container>
                <Grid item md={12}>
                    {/* Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    minHeight: '48px',
                                    color: '#666',
                                },
                                '& .Mui-selected': {
                                    color: '#008080 !important',
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#008080',
                                    height: '3px',
                                },
                            }}
                        >
                            {fullAccess && <Tab value={0} label="OVERVIEW" />}
                            {fullAccess && <Tab value={1} label="OPPORTUNITY" />}
                            {fullAccess && <Tab value={2} label="PROJECT MANAGEMENT" />}
                            <Tab value={9} label="PROJECT TIMELINE" />
                            {fullAccess && <Tab value={3} label="INVOICE SCHEDULE" />}
                            {fullAccess && <Tab value={4} label="RENEWAL" />}
                            {fullAccess && <Tab value={5} label="COLLECTIONS" />}
                            {fullAccess && <Tab value={6} label="ALERTS/TICKETS" />}
                            {fullAccess && <Tab value={7} label="SCHEDULES" />}
                            {fullAccess && <Tab value={8} label="MASTER DATA" />}
                        </Tabs>
                    </Box>

                    {/* Tab Content */}
                    {activeTab === 0 && <Overview />}
                    {activeTab === 1 && <Opportunity />}
                    {activeTab === 2 && <ProjectManagement />}
                    {activeTab === 3 && <InvoiceSchedule />}
                    {activeTab === 4 && (
                        <Renewal
                            queryParams={queryParams}
                            updateQueryParams={updateQueryParams}
                            setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                        />
                    )}
                    {activeTab === 5 && <Collections />}
                    {activeTab === 6 && <AlertsTickets />}
                    {activeTab === 7 && <MasterSchedules />}
                    {activeTab === 9 && <ProjectTimeline />}
                    {activeTab === 8 && (
                        <MasterData
                            data={data}
                            loading={loading}
                            totals={totals}
                            totalClients={totalClients}
                            totalClientGroups={totalClientGroups}
                            totalFacilities={totalFacilities}
                            totalSalesOrders={totalSalesOrders}
                            queryParams={queryParams}
                            handleSearch={handleSearch}
                            setShowFilterDialog={setShowFilterDialog}
                            handleRefreshCache={handleRefreshCache}
                            refreshing={refreshing}
                            handleDownload={handleDownload}
                            downloading={downloading}
                            showFilterDialog={showFilterDialog}
                            updateQueryParams={updateQueryParams}
                            setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                            handleTabSwitch={handleTabSwitch}
                            bdFilterOptions={bdFilterOptions}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            handlePageChange={handlePageChange}
                        />
                    )}

                </Grid>
            </Grid>
            <Snackbar
                open={!!notice.message}
                autoHideDuration={6000}
                onClose={() => setNotice({ message: '', severity: notice.severity })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotice({ message: '', severity: notice.severity })} severity={notice.severity} sx={{ width: '100%' }}>
                    {notice.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
